// Wave 8 — cylinder-body pressure mesh.
//
// The rider's body is approximated by the same 7 cylinders + head sphere
// that platform/shared/aero/sectional.py uses for per-region drag. The
// cylinders are positioned at MediaPipe joints every frame so the mesh
// tracks the rider's pose. Each cylinder gets a per-vertex pressure
// coefficient Cp(θ) from cross-flow theory:
//
//   Cp(θ) ≈ cos(θ),  θ = angle between vertex outward normal and the
//                     cross-flow wind direction.
//
// Stagnation (front, θ=0): Cp=+1 → bright red.
// Sides (θ=π/2): Cp=0 → yellow.
// Wake (back, θ=π): Cp=-1 → blue.
//
// This is honest: the same Hoerner cross-flow model is what computes the
// per-region drag watts the rider sees in the side stats. We're now
// showing pressure SPATIALLY on the body, not just per-region totals.

import * as THREE from 'three';
import { WorldLandmark } from './poseDetector';

// MediaPipe Pose landmark indices (subset we use).
const MP = {
  NOSE: 0,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
} as const;

export type BodyRegion = 'head' | 'torso' | 'arms' | 'legs';

export interface CylinderSpec {
  id: string;
  region: BodyRegion;
  /** Reference radius (metres) — width of the cylinder. Scaled per-rider
   *  by the cylinderBody update step. */
  radius: number;
}

// Eight body parts: head (sphere) + 7 cylinders. Limb cylinders are split
// into upper-arm + forearm + thigh + shank to give the right joint bends.
export const BODY_PARTS: CylinderSpec[] = [
  { id: 'head',          region: 'head',  radius: 0.10 },
  { id: 'torso',         region: 'torso', radius: 0.16 },
  { id: 'upper_arm_l',   region: 'arms',  radius: 0.045 },
  { id: 'upper_arm_r',   region: 'arms',  radius: 0.045 },
  { id: 'forearm_l',     region: 'arms',  radius: 0.038 },
  { id: 'forearm_r',     region: 'arms',  radius: 0.038 },
  { id: 'thigh_l',       region: 'legs',  radius: 0.075 },
  { id: 'thigh_r',       region: 'legs',  radius: 0.075 },
  { id: 'shank_l',       region: 'legs',  radius: 0.055 },
  { id: 'shank_r',       region: 'legs',  radius: 0.055 },
];

export interface BodyPartFrame {
  /** Midpoint of the part in world coordinates (metres, hip-centered, Y up). */
  center: THREE.Vector3;
  /** Unit vector along the part's axis (parent → child for cylinders). */
  axis: THREE.Vector3;
  /** Cylinder length (metres) — for the head sphere this is radius*2. */
  length: number;
  /** Reference radius (metres). */
  radius: number;
  /** Visibility ∈ [0,1] — minimum of both endpoint visibilities. */
  visibility: number;
  /** Which body region this part belongs to (for color intensity). */
  region: BodyRegion;
  /** "head" parts are rendered as spheres, not cylinders. */
  kind: 'sphere' | 'cylinder';
  id: string;
}

/** Reads MediaPipe world landmarks and returns the 10 body parts. */
export function bodyPartFramesFromLandmarks(
  world: WorldLandmark[],
): BodyPartFrame[] {
  // MediaPipe world landmarks: Y points DOWN in image convention. Convert
  // to Y-up (SMPL/Three.js convention) on read.
  const vec = (idx: number): THREE.Vector3 => {
    const lm = world[idx];
    return new THREE.Vector3(lm.x, -lm.y, lm.z);
  };
  const visMin = (...idxs: number[]): number => {
    return Math.min(...idxs.map((i) => world[i].visibility));
  };

  const midshoulder = vec(MP.L_SHOULDER).add(vec(MP.R_SHOULDER)).multiplyScalar(0.5);
  const midhip = vec(MP.L_HIP).add(vec(MP.R_HIP)).multiplyScalar(0.5);
  // Head center sits above the nose by roughly head_radius — the nose
  // landmark is on the front of the face, not the centroid.
  const headCenter = vec(MP.NOSE).clone().add(new THREE.Vector3(0, 0.05, 0));

  const cyl = (
    id: string,
    region: BodyRegion,
    radius: number,
    a: THREE.Vector3,
    b: THREE.Vector3,
    visibility: number,
  ): BodyPartFrame => {
    const center = a.clone().add(b).multiplyScalar(0.5);
    const ab = b.clone().sub(a);
    const length = ab.length();
    const axis = length > 1e-6 ? ab.divideScalar(length) : new THREE.Vector3(0, 1, 0);
    return { center, axis, length, radius, visibility, region, kind: 'cylinder', id };
  };

  return [
    // Head — sphere centered slightly above nose.
    {
      id: 'head',
      region: 'head',
      kind: 'sphere',
      center: headCenter,
      axis: new THREE.Vector3(0, 1, 0),
      length: 0.20,
      radius: 0.10,
      visibility: visMin(MP.NOSE),
    },
    // Torso — hip-midpoint to shoulder-midpoint.
    cyl('torso', 'torso', 0.16, midhip, midshoulder,
        visMin(MP.L_HIP, MP.R_HIP, MP.L_SHOULDER, MP.R_SHOULDER)),
    // Upper arms.
    cyl('upper_arm_l', 'arms', 0.045, vec(MP.L_SHOULDER), vec(MP.L_ELBOW),
        visMin(MP.L_SHOULDER, MP.L_ELBOW)),
    cyl('upper_arm_r', 'arms', 0.045, vec(MP.R_SHOULDER), vec(MP.R_ELBOW),
        visMin(MP.R_SHOULDER, MP.R_ELBOW)),
    // Forearms.
    cyl('forearm_l', 'arms', 0.038, vec(MP.L_ELBOW), vec(MP.L_WRIST),
        visMin(MP.L_ELBOW, MP.L_WRIST)),
    cyl('forearm_r', 'arms', 0.038, vec(MP.R_ELBOW), vec(MP.R_WRIST),
        visMin(MP.R_ELBOW, MP.R_WRIST)),
    // Thighs.
    cyl('thigh_l', 'legs', 0.075, vec(MP.L_HIP), vec(MP.L_KNEE),
        visMin(MP.L_HIP, MP.L_KNEE)),
    cyl('thigh_r', 'legs', 0.075, vec(MP.R_HIP), vec(MP.R_KNEE),
        visMin(MP.R_HIP, MP.R_KNEE)),
    // Shanks.
    cyl('shank_l', 'legs', 0.055, vec(MP.L_KNEE), vec(MP.L_ANKLE),
        visMin(MP.L_KNEE, MP.L_ANKLE)),
    cyl('shank_r', 'legs', 0.055, vec(MP.R_KNEE), vec(MP.R_ANKLE),
        visMin(MP.R_KNEE, MP.R_ANKLE)),
  ];
}

/** Pressure-coefficient color stops. Cp ∈ [-1, +1].
 *  Negative (wake) → blue. Zero (separation/transition) → green/yellow.
 *  Positive (stagnation) → red. */
const C_WAKE = new THREE.Color('#1e6cff');     // saturated blue
const C_MID  = new THREE.Color('#ffe44d');     // bright yellow
const C_FRONT = new THREE.Color('#ff4757');    // saturated red

export function cpColor(cp: number, out: THREE.Color = new THREE.Color()): THREE.Color {
  // Clamp Cp into the displayable range.
  const t = Math.max(0, Math.min(1, (cp + 1) / 2));
  if (t < 0.5) {
    out.copy(C_WAKE).lerp(C_MID, t * 2);
  } else {
    out.copy(C_MID).lerp(C_FRONT, (t - 0.5) * 2);
  }
  return out;
}

/** For a cylinder mesh aligned along its local Y axis (Three.js default),
 *  compute per-vertex Cp from cross-flow theory and write it into the
 *  geometry's color buffer.
 *
 *  windWorld is the wind direction in world coordinates (unit vector,
 *  pointing the way the wind blows toward the rider — usually -Z for a
 *  rider facing +Z).
 *
 *  intensity ∈ [0, 1] scales the saturation of the colors so body parts
 *  that contribute more drag look more vivid. */
export function paintCylinderPressure(
  mesh: THREE.Mesh,
  windWorld: THREE.Vector3,
  intensity: number,
): void {
  const geom = mesh.geometry as THREE.BufferGeometry;
  const positions = geom.attributes.position;
  if (!positions) return;

  // Make sure there's a color attribute we can write into.
  let colors = geom.attributes.color as THREE.BufferAttribute | undefined;
  if (!colors || colors.count !== positions.count) {
    colors = new THREE.BufferAttribute(new Float32Array(positions.count * 3), 3);
    geom.setAttribute('color', colors);
  }

  // Convert wind to the mesh's LOCAL frame (so we can compare against the
  // local-frame vertex positions cheaply).
  mesh.updateWorldMatrix(true, false);
  const localToWorld = mesh.matrixWorld.clone();
  const worldToLocal = new THREE.Matrix4().copy(localToWorld).invert();
  // Use a direction transform (no translation).
  const w = windWorld.clone();
  // Rotate-only: extract the upper 3x3 by zeroing the translation.
  const wLocal = w
    .clone()
    .transformDirection(worldToLocal);
  // For a cylinder with axis along local Y, the cross-flow plane is the
  // XZ plane. Project wind into that plane.
  const wPerpX = wLocal.x;
  const wPerpZ = wLocal.z;
  const wPerpMag = Math.hypot(wPerpX, wPerpZ);

  const color = new THREE.Color();
  const dim = new THREE.Color(0x404758); // muted background for low-flow regions
  const positionsArr = positions.array as Float32Array;
  const colorsArr = colors.array as Float32Array;

  // If there's effectively no cross-flow (wind is parallel to the
  // cylinder axis), paint a uniform neutral mid-color attenuated by
  // intensity. Physically: no pressure variation around the cylinder.
  if (wPerpMag < 1e-3) {
    cpColor(0, color);
    color.multiplyScalar(intensity).lerp(dim, 1 - intensity);
    for (let i = 0; i < positions.count; i++) {
      colorsArr[i * 3]     = color.r;
      colorsArr[i * 3 + 1] = color.g;
      colorsArr[i * 3 + 2] = color.b;
    }
    colors.needsUpdate = true;
    return;
  }

  const invMag = 1 / wPerpMag;
  for (let i = 0; i < positions.count; i++) {
    const x = positionsArr[i * 3];
    const z = positionsArr[i * 3 + 2];
    const radial = Math.hypot(x, z);
    if (radial < 1e-6) {
      // Endcap centerpoint — use zero pressure.
      cpColor(0, color);
    } else {
      // Outward radial direction in local XZ. cosTheta is the alignment
      // with the cross-flow wind direction.
      const cosTheta = (x * wPerpX + z * wPerpZ) / (radial * wPerpMag);
      // Cp(θ) — clamped cosine. Sharp enough for the heatmap to show a
      // clear stagnation → wake gradient without distracting bumps.
      const cp = Math.max(-1, Math.min(1, cosTheta * wPerpMag));
      cpColor(cp, color);
    }
    // Blend with a dim base color when intensity is low (low-drag parts
    // stay subtly visible without fighting for attention).
    color.multiplyScalar(intensity).lerp(dim, 1 - intensity);
    colorsArr[i * 3]     = color.r;
    colorsArr[i * 3 + 1] = color.g;
    colorsArr[i * 3 + 2] = color.b;
  }
  colors.needsUpdate = true;
}

/** Paint a sphere (head) with stagnation pressure on the front, wake on
 *  the back. Same Cp(θ) where θ = angle between vertex normal and wind. */
export function paintSpherePressure(
  mesh: THREE.Mesh,
  windWorld: THREE.Vector3,
  intensity: number,
): void {
  const geom = mesh.geometry as THREE.BufferGeometry;
  const positions = geom.attributes.position;
  if (!positions) return;

  let colors = geom.attributes.color as THREE.BufferAttribute | undefined;
  if (!colors || colors.count !== positions.count) {
    colors = new THREE.BufferAttribute(new Float32Array(positions.count * 3), 3);
    geom.setAttribute('color', colors);
  }

  mesh.updateWorldMatrix(true, false);
  const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const wLocal = windWorld.clone().transformDirection(worldToLocal);
  const wMag = wLocal.length();
  if (wMag < 1e-6) wLocal.set(0, 0, 1);
  else wLocal.divideScalar(wMag);

  const color = new THREE.Color();
  const dim = new THREE.Color(0x404758);
  const positionsArr = positions.array as Float32Array;
  const colorsArr = colors.array as Float32Array;

  for (let i = 0; i < positions.count; i++) {
    const x = positionsArr[i * 3];
    const y = positionsArr[i * 3 + 1];
    const z = positionsArr[i * 3 + 2];
    const r = Math.hypot(x, y, z);
    if (r < 1e-6) {
      cpColor(0, color);
    } else {
      const cosTheta = (x * wLocal.x + y * wLocal.y + z * wLocal.z) / r;
      cpColor(Math.max(-1, Math.min(1, cosTheta)), color);
    }
    color.multiplyScalar(intensity).lerp(dim, 1 - intensity);
    colorsArr[i * 3]     = color.r;
    colorsArr[i * 3 + 1] = color.g;
    colorsArr[i * 3 + 2] = color.b;
  }
  colors.needsUpdate = true;
}

/** Convert per-region drag watts into a per-part intensity ∈ [0,1] for
 *  the cylinder coloring. The highest-drag region gets 1.0; everything
 *  else scales proportionally with a floor so low-drag parts remain
 *  visible (intensity stays above 0.25). */
export function intensitiesFromWatts(
  regionWatts: { head?: number; torso?: number; arms?: number; legs?: number },
): Record<BodyRegion, number> {
  const w = {
    head:  regionWatts.head  ?? 0,
    torso: regionWatts.torso ?? 0,
    arms:  regionWatts.arms  ?? 0,
    legs:  regionWatts.legs  ?? 0,
  };
  const max = Math.max(w.head, w.torso, w.arms, w.legs, 1e-3);
  const floor = 0.25;
  const norm = (x: number) => floor + (1 - floor) * (x / max);
  return {
    head:  norm(w.head),
    torso: norm(w.torso),
    arms:  norm(w.arms),
    legs:  norm(w.legs),
  };
}

/** Helper — orient a Three.js mesh so its local +Y axis points along a
 *  given world direction. Cylinder + capsule geometries are Y-aligned
 *  by default, so this is the standard "stand the cylinder up along an
 *  arbitrary bone direction" trick. */
export function alignMeshToAxis(mesh: THREE.Mesh, axisWorld: THREE.Vector3): void {
  const up = new THREE.Vector3(0, 1, 0);
  const dir = axisWorld.clone().normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(up, dir);
  mesh.quaternion.copy(q);
}
